import React, { lazy, useRef, useEffect, useMemo, Suspense } from "react";
import { useSelector, useDispatch } from "react-redux";
import { setOverGallery } from "../../redux/nav/nav.actions";
import MediaQuery from "react-responsive";
import { useLocation } from "react-router-dom";

import Home from "../../components/home/home.component";
import Loader from "../../components/loader/loader.component";
import DeferredSection from "../../components/deferred-section/deferred-section.component";
import HomePageSeo from "../../seo/homepage-seo.component";

import "./homepage.styles.scss";

const OurStory = lazy(
  () => import("../../components/our-story/our-story.component"),
);

const Gallery = lazy(
  () => import("../../components/gallery/gallery.component"),
);

const Contact = lazy(
  () => import("../../components/contact/contact.component"),
);
const PostsSection = lazy(
  () => import("../../components/posts-section/posts-section.component"),
);
const Faqs = lazy(() => import("../../components/faqs/faqs.component"));

export const GALLERY_SECTION_WIDTH = 1025;

interface HomePageState {
  nav: {
    section: string | null;
  };
  menu: {
    showMenu: boolean;
  };
}

const HomePage: React.FC = () => {
  const dispatch = useDispatch();
  const { hash, pathname } = useLocation();
  const anchor = hash.slice(1);
  const hasAnchor = ["bookings", "sheung-wan", "causeway-bay", "faqs", "our-story", "journal"].includes(anchor);
  const pageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const page = pageRef.current;
    if (!hasAnchor || !page) return;

    // Wait for every deferred section to commit so preceding content has height.
    const scrollToAnchor = () => {
      const target = document.getElementById(anchor);
      if (!target || page.querySelector("[data-section-loading]")) return;
      target.scrollIntoView({ block: "start", behavior: "auto" });
      observer.disconnect();
    };
    const observer = new MutationObserver(scrollToAnchor);
    observer.observe(page, { childList: true, subtree: true });
    scrollToAnchor();
    return () => observer.disconnect();
  }, [anchor, hasAnchor, pathname]);
  const section = useSelector((state: HomePageState) => state.nav.section);
  const isMenuOpen = useSelector((state: HomePageState) => state.menu.showMenu);

  const homeRef = useRef<HTMLDivElement>(null);
  const ourStoryRef = useRef<HTMLDivElement>(null);
  const galleryRef = useRef<HTMLDivElement>(null);
  const contactRef = useRef<HTMLDivElement>(null);
  // const updatesRef = useRef<HTMLDivElement>(null);
  const postsRef = useRef<HTMLDivElement>(null);
  const faqsRef = useRef<HTMLDivElement>(null);

  const refMap = useMemo<Record<string, React.RefObject<HTMLDivElement>>>(
    () => ({
      Home: homeRef,
      OurStory: ourStoryRef,
      Gallery: galleryRef,
      Contact: contactRef,
      Posts: postsRef,
      Faqs: faqsRef,
    }),
    [homeRef, ourStoryRef, galleryRef, contactRef, postsRef, faqsRef],
  );

  useEffect(() => {
    const handleScroll = () => {
      if (!galleryRef.current) return;
      const { top, bottom } = galleryRef.current.getBoundingClientRect();
      dispatch(setOverGallery(top <= 80 && bottom >= 0));
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [dispatch, galleryRef]);

  useEffect(() => {
    if (section && refMap[section]) {
      const ref = refMap[section];
      if (ref.current) {
        const scrollTimeoutId = window.setTimeout(() => {
          ref.current?.scrollIntoView({ behavior: "smooth" });
        }, 300);

        return () => {
          window.clearTimeout(scrollTimeoutId);
        };
      }
    }
  }, [section, refMap]);

  return (
    <div className="home-page" ref={pageRef}>
      <HomePageSeo />
      <div ref={homeRef}>
        <Home />
      </div>
      <div ref={ourStoryRef} id="our-story" className="sitelink-anchor">
        <DeferredSection forceRender={isMenuOpen || hasAnchor}>
          <Suspense fallback={<div data-section-loading><Loader /></div>}>
            <OurStory />
          </Suspense>
        </DeferredSection>
      </div>
      <MediaQuery minWidth={GALLERY_SECTION_WIDTH}>
        <div ref={galleryRef}>
          <DeferredSection forceRender={isMenuOpen || hasAnchor}>
            <Suspense fallback={<div data-section-loading><Loader /></div>}>
              <Gallery />
            </Suspense>
          </DeferredSection>
        </div>
      </MediaQuery>
      <div ref={contactRef} id="bookings" className="sitelink-anchor">
        <DeferredSection forceRender={isMenuOpen || hasAnchor}>
          <Suspense fallback={<div data-section-loading><Loader /></div>}>
            <Contact />
          </Suspense>
        </DeferredSection>
      </div>
      <div ref={postsRef} id="journal" className="sitelink-anchor">
        <DeferredSection forceRender={isMenuOpen || hasAnchor}>
          <Suspense fallback={<div data-section-loading><Loader /></div>}>
            <PostsSection />
          </Suspense>
        </DeferredSection>
      </div>
      <div ref={faqsRef} id="faqs" className="sitelink-anchor">
        <DeferredSection forceRender={isMenuOpen || hasAnchor}>
          <Suspense fallback={<div data-section-loading><Loader /></div>}>
            <Faqs />
          </Suspense>
        </DeferredSection>
      </div>
    </div>
  );
};

export default HomePage;
